// Shared accessibility primitives.
//
// - announce(): push a message to a screen-reader live region from anywhere
//   (including non-React code like fetch handlers), so async state changes
//   ("Summary ready", "Generating…", errors) are spoken.
// - <A11yLiveRegions/>: mounts the polite/assertive regions early.
// - <SkipLink/>: keyboard "skip to main content".
// - <RouteFocus/>: on navigation, move focus to <main> and announce the page,
//   so a screen-reader user isn't stranded on the old element after a SPA route
//   change.
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

let politeEl: HTMLElement | null = null;
let assertiveEl: HTMLElement | null = null;

function ensureLiveRegions(): void {
  if (typeof document === 'undefined' || politeEl) return;
  const make = (live: string): HTMLElement => {
    const el = document.createElement('div');
    el.setAttribute('aria-live', live);
    el.setAttribute('aria-atomic', 'true');
    el.className = 'sr-only';
    document.body.appendChild(el);
    return el;
  };
  politeEl = make('polite');
  assertiveEl = make('assertive');
}

/** Announce a message to assistive technology. Use `assertive` only for
 * things that should interrupt (errors); default is polite. */
export function announce(message: string, opts: { assertive?: boolean } = {}): void {
  if (!message) return;
  ensureLiveRegions();
  const el = opts.assertive ? assertiveEl : politeEl;
  if (!el) return;
  // Clear first so an identical consecutive message is re-announced.
  el.textContent = '';
  window.requestAnimationFrame(() => {
    if (el) el.textContent = message;
  });
}

/** Mount once near the app root so the live regions exist before any announce. */
export const A11yLiveRegions: React.FC = () => {
  useEffect(() => {
    ensureLiveRegions();
  }, []);
  return null;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Trap focus inside `ref` while `active`. Focuses the first focusable element
 * on open, cycles Tab/Shift+Tab within the container, closes on Escape, and
 * restores focus to the previously-focused element on close. Use for modals/
 * dialogs. */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement>,
  active: boolean,
  onClose?: () => void,
): void {
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = () =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    // Focus the first focusable element (or the container itself).
    const first = focusables()[0];
    if (first) first.focus();
    else {
      container.setAttribute('tabindex', '-1');
      container.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const activeEl = document.activeElement as HTMLElement;
      if (e.shiftKey && activeEl === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && activeEl === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // Restore focus to whatever was focused before the dialog opened.
      previouslyFocused?.focus?.();
    };
  }, [active, ref, onClose]);
}

export const MAIN_CONTENT_ID = 'main-content';

/** Keyboard-first "skip to main content" link — visually hidden until focused. */
export const SkipLink: React.FC = () => (
  <a
    href={`#${MAIN_CONTENT_ID}`}
    className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-gray-900 focus:shadow-lg focus:ring-2 focus:ring-emerald-500"
  >
    Skip to main content
  </a>
);

/** On route change, move focus to the main region and announce the page's
 * heading so screen-reader users get oriented. */
export const RouteFocus: React.FC = () => {
  const { pathname } = useLocation();
  const firstRender = useRef(true);
  useEffect(() => {
    // Don't steal focus on the very first paint (the user hasn't navigated yet).
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const id = window.setTimeout(() => {
      const main = document.getElementById(MAIN_CONTENT_ID);
      if (!main) return;
      const heading = main.querySelector('h1, h2');
      const label = (heading?.textContent || document.title || 'Page').trim();
      main.focus({ preventScroll: false });
      announce(label ? `${label}` : 'Navigated');
    }, 60);
    return () => window.clearTimeout(id);
  }, [pathname]);
  return null;
};
